/**
 * Common error types for link-storage
 * Uses Effect Data.TaggedEnum for discriminated error handling
 */

import { Data } from "effect";

/**
 * Base application errors using taggedEnum for type-safe error discrimination
 */
export type AppError = Data.TaggedEnum<{
  NotFoundError: { readonly id: string; readonly entity: string };
  ValidationError: { readonly field: string; readonly message: string };
  DatabaseError: { readonly message: string; readonly cause?: unknown };
  UnknownError: { readonly message: string; readonly cause?: unknown };
}>;

export const AppError = Data.taggedEnum<AppError>();

/**
 * Creates a NotFoundError
 */
export const notFoundError = (id: string, entity: string) =>
  AppError.NotFoundError({ id, entity });

/**
 * Creates a ValidationError
 */
export const validationError = (field: string, message: string) =>
  AppError.ValidationError({ field, message });

/**
 * Creates a DatabaseError
 */
export const databaseError = (message: string, cause?: unknown) =>
  AppError.DatabaseError({ message, cause });

/**
 * Creates an UnknownError
 */
export const unknownError = (message: string, cause?: unknown) =>
  AppError.UnknownError({ message, cause });

/**
 * Type guards for error discrimination
 */
export const isNotFoundError = (
  error: AppError
): error is Extract<AppError, { _tag: "NotFoundError" }> =>
  error._tag === "NotFoundError";

export const isValidationError = (
  error: AppError
): error is Extract<AppError, { _tag: "ValidationError" }> =>
  error._tag === "ValidationError";

export const isDatabaseError = (
  error: AppError
): error is Extract<AppError, { _tag: "DatabaseError" }> =>
  error._tag === "DatabaseError";

export const isUnknownError = (
  error: AppError
): error is Extract<AppError, { _tag: "UnknownError" }> =>
  error._tag === "UnknownError";

/**
 * Maps unknown errors to AppError
 */
export const toAppError = (error: unknown): AppError => {
  // Check if it's already an AppError by checking for _tag property
  if (
    typeof error === "object" &&
    error !== null &&
    "_tag" in error &&
    (error._tag === "NotFoundError" ||
      error._tag === "ValidationError" ||
      error._tag === "DatabaseError" ||
      error._tag === "UnknownError")
  ) {
    return error as AppError;
  }

  if (error instanceof Error) {
    return unknownError(error.message, error);
  }

  return unknownError("An unknown error occurred", error);
};

/**
 * Error message formatters
 */
export const formatError = (error: AppError): string => {
  switch (error._tag) {
    case "NotFoundError":
      return `${error.entity} with id '${error.id}' not found`;
    case "ValidationError":
      return `Validation failed for '${error.field}': ${error.message}`;
    case "DatabaseError":
      return `Database error: ${error.message}`;
    case "UnknownError":
      return `Unknown error: ${error.message}`;
  }
};
